import { cn } from '../lib/utils';
import { Label } from '../primitives/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../primitives/select';
import { DatePicker } from './date-picker';
import {
  DATE_RANGE_PRESET_LABELS,
  DATE_RANGE_PRESETS,
  type DateRangePreset,
  type DateRangeValue,
  resolveDateRange,
} from './model';

/**
 * Shared date-range filter: a preset dropdown (current/previous month ·
 * quarter · year · Custom) above two always-visible From/To pickers. Picking
 * a macro fills the pickers with its computed window; editing either picker
 * switches the selection to Custom. Built for reuse across reports and any
 * page that scopes data to a date window — own the value in URL search params
 * (bookmarkable) or local state and feed it in; the component is controlled
 * and stateless.
 *
 * Pair the rendered control with `resolveDateRange(value)` to turn the
 * selection into a concrete `{ from, to }` (or `null` when a Custom range is
 * incomplete/invalid) for queries and exports.
 */
export interface DateRangeFilterProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  /** Label above the preset select. Pass `null` to omit. */
  label?: string | null;
  /** Presets to offer; defaults to the standard list (excludes `all_time`). */
  presets?: readonly DateRangePreset[];
  id?: string;
  className?: string;
  /**
   * `'stacked'` (default): the preset select sits above the From/To pickers —
   * right for a narrow sidebar-style filter column.
   * `'inline'`: preset select and From/To sit side by side as one row —
   * right for a horizontal filter bar alongside other single-row controls.
   */
  layout?: 'stacked' | 'inline';
}

export function DateRangeFilter({
  value,
  onChange,
  label = 'Date range',
  presets = DATE_RANGE_PRESETS,
  id = 'date-range',
  className,
  layout = 'stacked',
}: DateRangeFilterProps) {
  const isCustom = value.preset === 'custom';
  // "All time" has no window, so the From/To pickers don't apply.
  const isAllTime = value.preset === 'all_time';
  const resolved = resolveDateRange(value);
  // Dates shown in the always-visible pickers: a macro's computed window, or
  // the user's explicit custom dates.
  const shownFrom = isCustom ? (value.from ?? '') : (resolved?.from ?? '');
  const shownTo = isCustom ? (value.to ?? '') : (resolved?.to ?? '');
  const customInvalid =
    isCustom && resolved === null && (value.from !== undefined || value.to !== undefined);

  const handlePreset = (preset: DateRangePreset) => {
    // Switching to Custom keeps the currently-shown window as a starting
    // point so the pickers don't blank out.
    if (preset === 'custom') {
      onChange({ preset: 'custom', from: shownFrom || undefined, to: shownTo || undefined });
    } else {
      onChange({ preset });
    }
  };

  // Editing either picker means the window is no longer a named macro → Custom.
  // The other bound carries over from whatever was shown (the macro's window).
  const editFrom = (v: string) =>
    onChange({ preset: 'custom', from: v || undefined, to: shownTo || undefined });
  const editTo = (v: string) =>
    onChange({ preset: 'custom', from: shownFrom || undefined, to: v || undefined });

  const presetSelect = (
    <Select value={value.preset} onValueChange={(v) => handlePreset(v as DateRangePreset)}>
      <SelectTrigger
        id={id}
        aria-label={label ?? 'Date range'}
        className={layout === 'inline' ? 'w-44' : 'w-full'}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {presets.map((p) => (
          <SelectItem key={p} value={p}>
            {DATE_RANGE_PRESET_LABELS[p]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const fromPicker = (
    <div className="grid gap-1.5">
      <DatePicker
        id={`${id}-from`}
        value={shownFrom}
        onChange={editFrom}
        placeholder="From"
        className="w-40"
        ariaInvalid={customInvalid}
      />
    </div>
  );

  const toPicker = (
    <div className="grid gap-1.5">
      <DatePicker
        id={`${id}-to`}
        value={shownTo}
        onChange={editTo}
        placeholder="To"
        className="w-40"
        ariaInvalid={customInvalid}
      />
    </div>
  );

  const invalidText = customInvalid
    ? 'Pick a “From” and “To” date — “To” must be on or after “From”.'
    : null;

  if (layout === 'inline') {
    return (
      <div className={cn('flex flex-wrap items-end gap-3', className)}>
        <div className="grid gap-1.5">
          {label ? <Label htmlFor={id}>{label}</Label> : null}
          {presetSelect}
        </div>
        {isAllTime ? null : (
          <>
            {fromPicker}
            {toPicker}
          </>
        )}
        {invalidText ? <p className="w-full text-sm text-destructive">{invalidText}</p> : null}
      </div>
    );
  }

  return (
    <div className={cn('grid gap-1.5', className)}>
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      {presetSelect}
      {isAllTime ? null : (
        <div className="flex flex-wrap items-end gap-3 pt-1">
          {fromPicker}
          {toPicker}
        </div>
      )}
      {invalidText ? <p className="text-sm text-destructive">{invalidText}</p> : null}
    </div>
  );
}
